"use client";

import dynamic from "next/dynamic";
import Link from "next/link";

import Button from "@/components/atoms/Button";
import DrupalImage from "@/components/atoms/DrupalImage";
import PlayBtn from "@/components/atoms/PlayBtn";
import {
  ImageFieldsFragment,
  ImageStyleAvailable,
  MediaTextParagraphFieldsFragment,
} from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import { getVariation } from "@/helpers/image";
import { tailwindTheme } from "@/lib/constants/tailwindTheme";
import { withRetry } from "@/lib/helpers/dynamicRetryImport";

const VideoPlayerNoSSR = dynamic(
  withRetry(() => import("@/components/atoms/VideoPlayer")),
  {
    ssr: false,
    loading: () => (
      <div className="aspect-video w-full animate-pulse rounded-2xl bg-gray-200" />
    ),
  },
);

interface MediaTextParagraphProps {
  className?: string;
  data: MediaTextParagraphFieldsFragment;
  priority?: boolean;
}

export const MediaTextParagraph = ({
  data,
  className,
  priority,
}: MediaTextParagraphProps) => {
  const imageLeft = data.entity_type_behaviors?.media_position === "left";
  const textWidth33 = data.entity_type_behaviors?.text_width === "33";
  const textWidth50 = data.entity_type_behaviors?.text_width === "55";
  const textWidth66 = data.entity_type_behaviors?.text_width === "66";
  let videoUrl = "";
  let posterUrl: string | undefined = undefined;

  // Select the image size based on the media column's actual rendered width, which depends on text_width (33/50/66).
  const mediaColumnVw = textWidth33 ? 66 : textWidth66 ? 33 : 50;
  const mediaImageSizes = `(min-width: ${tailwindTheme.screens.lg}) ${mediaColumnVw}vw, 100vw`;

  if (
    data.paragraphMediaTextMedia &&
    "mediaOembedVideo" in data.paragraphMediaTextMedia &&
    data.paragraphMediaTextMedia.mediaOembedVideo
  ) {
    videoUrl = data.paragraphMediaTextMedia?.mediaOembedVideo ?? "";
    const videoId = videoUrl.includes("embed/")
      ? videoUrl.split("embed/")[1]
      : videoUrl.split("v=")[1];

    posterUrl = data.paragraphMediaTextMedia.remoteVideoMediaImage?.variations
      ? getVariation(
          data.paragraphMediaTextMedia.remoteVideoMediaImage.variations,
          ImageStyleAvailable.W3800,
        )?.url
      : `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  }

  return (
    <div className={cn(className, "paragraph--media-text container")}>
      <div
        className={cn(
          "flex flex-col lg:flex-row",
          !imageLeft && "lg:flex-row-reverse",
        )}
      >
        <div
          className={cn(
            "mb-8 flex basis-1/2 flex-col justify-center lg:mb-0",
            textWidth33 && "basis-2/3",
            textWidth50 && "basis-1/2",
            textWidth66 && "basis-1/3",
          )}
        >
          {videoUrl ? (
            <div className="aspect-video w-full overflow-hidden rounded-2xl">
              <VideoPlayerNoSSR
                playBtn={<PlayBtn />}
                videoUrl={videoUrl}
                posterUrl={posterUrl}
              />
            </div>
          ) : (
            <MediaImage data={data} sizes={mediaImageSizes} priority={priority} />
          )}
        </div>
        <div
          className={cn(
            "flex basis-1/2 flex-col justify-center lg:p-2 lg:px-8 xl:px-12 2xl:px-16",
            textWidth33 && "basis-1/3",
            textWidth50 && "basis-1/2",
            textWidth66 && "basis-2/3",
          )}
        >
          {data.label && (
            <div className="h4 caption !text-sm text-orange">{data.label}</div>
          )}
          {data.title && <h2>{data.title}</h2>}

          <div
            className="prose"
            dangerouslySetInnerHTML={{
              __html: data.paragraphMediaTextText.processed ?? "",
            }}
          />

          {data.links && (
            <div className="mt-8 flex flex-wrap items-center gap-6 lg:mt-12 lg:gap-8">
              {data.links.map((link, index) =>
                index === 0 ? (
                  <Button
                    as="link"
                    key={link.url}
                    href={link.url ?? "#"}
                    arrow={data.links?.length === 1}
                    className="btn-lg"
                  >
                    {link.title}
                  </Button>
                ) : (
                  <Link key={link.url} href={link.url ?? "#"} className="link">
                    {link.title}
                  </Link>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface MediaImageProps {
  data: MediaTextParagraphFieldsFragment;
  priority?: boolean;
  sizes: string;
}
const MediaImage = ({ data, sizes, priority }: MediaImageProps) => {
  const paragraphMediaTextMedia = data.paragraphMediaTextMedia;
  const paragraphMediaTextLinkForMedia = data.paragraphMediaTextLinkForMedia;

  if (
    !paragraphMediaTextMedia ||
    !("imageMediaImage" in paragraphMediaTextMedia) ||
    !paragraphMediaTextMedia.imageMediaImage ||
    !paragraphMediaTextMedia.imageMediaImage?.variations
  ) {
    return null;
  }

  const mediaImage: ImageFieldsFragment =
    paragraphMediaTextMedia.imageMediaImage;
  const image = getVariation(
    paragraphMediaTextMedia.imageMediaImage.variations,
    ImageStyleAvailable.W2000,
  );

  if (!image) {
    return null;
  }

  const imageElement = (
    <DrupalImage
      url={image.url}
      alt={mediaImage?.alt ?? ""}
      width={image.width}
      height={image.height}
      sizes={sizes}
      className={"w-full rounded-2xl"}
      priority={priority}
      fetchPriority={priority ? "high" : undefined}
    />
  );

  const linkURL = paragraphMediaTextLinkForMedia?.url;
  if (linkURL) {
    return (
      <Link
        href={linkURL}
        target={paragraphMediaTextLinkForMedia?.internal ? "_self" : "_blank"}
      >
        {imageElement}
      </Link>
    );
  }

  return <>{imageElement}</>;
};
